import type { EmergencyAccessSessionDetail } from '@usacsmideo/types';

/**
 * Family-facing badge derivation for the Sessions Surface (CU-86aj1c9ev),
 * implementing the APPROVED Timeline Filter Rules decision (CU-86ahjxn9e §D2)
 * verbatim. Two classes:
 *   - "alert"  — engagement gap (responder didn't fully view the wishes).
 *   - "error"  — technical/consent failure (e.g. revoked).
 *   - "info"   — neutral/positive informational badges (accessed, watched, …).
 *
 * Pure function — no rendering — so the taxonomy is unit-testable in isolation.
 */
export type SessionBadgeClass = 'alert' | 'error' | 'info';

export interface SessionBadge {
  label: string;
  badgeClass: SessionBadgeClass;
}

const hasEvent = (s: EmergencyAccessSessionDetail, type: string): boolean =>
  (s.events ?? []).some((e) => e.type === type);

/**
 * Derive the ordered badge list for one session card. Order is deliberate:
 * primary state first (Accessed / Returned-after-expiry / Error), then
 * engagement detail (watched / documents), then the alert verdict last so it
 * reads as the headline follow-up flag.
 */
export function deriveSessionBadges(
  session: EmergencyAccessSessionDetail
): SessionBadge[] {
  const badges: SessionBadge[] = [];

  // D2 / D4 — primary outcome state.
  if (session.outcome === 'revoked') {
    badges.push({ label: 'Access revoked', badgeClass: 'error' });
  } else if (session.outcome === 'post-expiry') {
    badges.push({ label: 'Returned after expiry', badgeClass: 'info' });
  } else if (session.outcome === 'success') {
    badges.push({ label: 'Accessed', badgeClass: 'info' });
  } else {
    // expired / not-found / error — surfaced as a generic failure (not bot:
    // bot is filtered server-side and never reaches this surface).
    badges.push({ label: 'Could not be opened', badgeClass: 'error' });
  }

  // "Reviewed again ×N" — rerenderCount > 0.
  if (typeof session.rerenderCount === 'number' && session.rerenderCount > 0) {
    badges.push({
      label: `Reviewed again ×${session.rerenderCount}`,
      badgeClass: 'info',
    });
  }

  // Engagement detail from the event stream.
  if (hasEvent(session, 'video_progress_90')) {
    badges.push({ label: 'Watched 90%', badgeClass: 'info' });
  } else if (hasEvent(session, 'video_progress_25')) {
    badges.push({ label: 'Watched 25%', badgeClass: 'info' });
  }
  if (hasEvent(session, 'pdf_opened')) {
    badges.push({ label: 'Documents opened', badgeClass: 'info' });
  }

  // Alert verdict last — the engagement-gap headline / its resolution.
  if (session.alert) {
    badges.push(
      session.alert.status === 'resolved'
        ? { label: 'Alert resolved', badgeClass: 'alert' }
        : { label: 'Alert raised', badgeClass: 'alert' }
    );
  }

  return badges;
}
